// RegistroAtendimento — exibe o laudo de fechamento da OS:
//   tipo_encerramento + classificacao (como badges)
//   descricao_relato_fechamento (formatado, com campos quebrados em linhas)
//   lista de materiais consumidos (tabela)
//
// O relato do Voalle vem como TEXTO LIVRE sem quebras de linha. Os técnicos
// escrevem coisas como "NOME: João  PATRIMONIO: 12345  ROT: 069090..." tudo
// numa string. Esse componente detecta padrões "PALAVRA EM CAIXA:" e quebra
// em linhas pra leitura melhor.

import { useMemo } from 'react';

function formatBRL(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatQtd(q, un) {
  if (q == null) return '—';
  const n = Number(q);
  const valor = Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, '');
  return `${valor} ${un || ''}`.trim();
}

// Quebra o relato em "campos" quando detecta padrão "ROTULO:" (texto curto + dois pontos)
function parsearRelato(texto) {
  if (!texto) return [];
  // Quebra em campos: split por padrão de "PALAVRA(S) EM CAIXA + :"
  // Aceita 2 a 60 caracteres antes do : (palavras maiúsculas com possíveis acentos/espaços/números)
  const linhas = [];
  const regex = /([A-ZÀ-ÚÇ][A-ZÀ-ÚÇ0-9 \/\.\-\(\)º]{1,60}?:)/g;
  let lastIdx = 0;
  let lastLabel = null;

  const matches = [...texto.matchAll(regex)];
  if (matches.length === 0) return [{ label: null, valor: texto }];

  for (const m of matches) {
    if (lastLabel != null) {
      linhas.push({
        label: lastLabel.trim(),
        valor: texto.slice(lastIdx, m.index).trim().replace(/^[:\s•·]+|[•·]+$/g, ''),
      });
    } else if (m.index > 0) {
      // Texto antes do primeiro label (rara, mas captura)
      const head = texto.slice(0, m.index).trim();
      if (head) linhas.push({ label: null, valor: head });
    }
    lastLabel = m[0].replace(/:$/, '');
    lastIdx = m.index + m[0].length;
  }
  if (lastLabel != null) {
    linhas.push({
      label: lastLabel.trim(),
      valor: texto.slice(lastIdx).trim().replace(/^[:\s•·]+|[•·]+$/g, ''),
    });
  }
  return linhas.filter(l => l.valor !== '' || l.label);
}

export default function RegistroAtendimento({ visita }) {
  if (!visita) return null;

  const camposRelato = useMemo(() => parsearRelato(visita.descricao_relato_fechamento), [visita.descricao_relato_fechamento]);
  const materiais = visita.materiais || [];
  const valorMateriais = visita.materiais_valor_total || 0;

  const temConteudo = visita.tipo_encerramento || visita.classificacao || visita.descricao_relato_fechamento || materiais.length > 0;
  if (!temConteudo) return null;

  // Cor do badge de tipo_encerramento por palavra-chave
  function corEncerramento(s) {
    if (!s) return { bg: '#f3f4f6', txt: '#374151' };
    const l = s.toLowerCase();
    if (l.includes('realiz') || l.includes(' ok')) return { bg: '#dcfce7', txt: '#14532d' };
    if (l.includes('não tratado') || l.includes('nao tratado') || l.includes('sem interação')) return { bg: '#fee2e2', txt: '#7f1d1d' };
    if (l.includes('erro') || l.includes('defeito')) return { bg: '#fef3c7', txt: '#78350f' };
    if (l.includes('resolvido')) return { bg: '#dbeafe', txt: '#1e3a8a' };
    return { bg: '#f3f4f6', txt: '#374151' };
  }
  const corEnc = corEncerramento(visita.tipo_encerramento);

  return (
    <div className="card">
      <h3 style={{ marginTop: 0, marginBottom: 12 }}>📋 Registro do atendimento</h3>

      {/* Badges de tipo de encerramento + classificação */}
      {(visita.tipo_encerramento || visita.classificacao) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {visita.tipo_encerramento && (
            <span style={{
              padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              background: corEnc.bg, color: corEnc.txt,
            }}>
              {visita.tipo_encerramento}
            </span>
          )}
          {visita.classificacao && (
            <span style={{
              padding: '4px 10px', borderRadius: 999, fontSize: 12,
              background: '#f3f4f6', color: '#374151',
            }}>
              {visita.classificacao}
            </span>
          )}
        </div>
      )}

      {/* Relato livre — formatado */}
      {visita.descricao_relato_fechamento && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Relato do técnico
          </div>
          <div style={{
            background: '#fafafa', border: '1px solid #f3f4f6',
            borderRadius: 8, padding: '10px 12px',
          }}>
            {camposRelato.length <= 1 ? (
              <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {visita.descricao_relato_fechamento}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 13 }}>
                {camposRelato.map((l, i) => (
                  l.label ? (
                    <div key={i} style={{ display: 'contents' }}>
                      <strong style={{ color: '#6b7280', fontWeight: 600, fontSize: 12 }}>{l.label}:</strong>
                      <span style={{ color: '#111827' }}>{l.valor || <span className="muted">—</span>}</span>
                    </div>
                  ) : (
                    <div key={i} style={{ gridColumn: '1 / -1', color: '#374151', fontStyle: 'italic' }}>
                      {l.valor}
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabela de materiais */}
      {materiais.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Materiais consumidos · {materiais.length}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>
              {formatBRL(valorMateriais)}
            </div>
          </div>
          <div style={{ border: '1px solid #f3f4f6', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                  <th style={{ padding: '6px 10px', fontWeight: 600, color: '#6b7280' }}>Produto</th>
                  <th style={{ padding: '6px 10px', fontWeight: 600, color: '#6b7280', textAlign: 'right', whiteSpace: 'nowrap' }}>Qtd</th>
                  <th style={{ padding: '6px 10px', fontWeight: 600, color: '#6b7280', textAlign: 'right', whiteSpace: 'nowrap' }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {materiais.map((m, i) => (
                  <tr key={m.id} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                    <td style={{ padding: '6px 10px' }}>
                      <div style={{ color: '#111827' }}>{m.produto_descricao}</div>
                      {m.produto_grupo_descricao && (
                        <div style={{ fontSize: 10, color: '#9ca3af' }}>{m.produto_grupo_descricao}</div>
                      )}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#4b5563' }}>
                      {formatQtd(m.quantidade, m.unidade_medida)}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#111827' }}>
                      {formatBRL(m.valor_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
